import {
    CommonTypes,
    lodash,
    Logger,
    PreloaderInfo,
    PreloaderProviderBase,
    PreloaderResourceItem,
    PreloaderResourceLoader,
    StringUtils,
    UriHelper,
} from "neo-common";

import { Platforms } from "neo-games";
import { XmlResource } from "../Resources/XmlResource";
import { SoundManager } from "./SoundManager";
import { StyleManager } from "./StyleManager";
import { CanvasManager } from "./CanvasManager";
import { ConfigurationManager } from "./ConfigurationManager";
import { HtmlManager } from "./HtmlManager";
import { StageController } from "../StageController";
import { ActionManager } from "./ActionManager";
import { AppContext } from "../../../Common/AppContext";
import { GameApp } from "../../GameApp";
import { ILoadingElement } from "../Elements/Interfaces/ILoadingElement";
import { CustomUtils } from "../../Utils/CustomUtils";
import { DataManager } from "../../Managers/DataManager";

const DOCUMENT_TYPE = "document";

const PIXI_EVENTS = {
    Load: "load",
    Error: "error",
    Complete: "complete",
};

const LOADJS_EVENTS = {
    FileLoad: "fileload",
    Complete: "complete",
    Progress: "progress",
    Error: "error",
};

const RESOURCE_FLAGS = {
    Gamble: "G",
};

export class PreloaderProvider extends PreloaderProviderBase {
    private static _loadingElements: Array<ILoadingElement> = [];
    private static _loadedGroups: Array<string> = [];

    private _resourceLoader: PIXIResourceLoader;
    private _audioResourceLoader: PIXIResourceLoader;

    get resourceLoader(): PreloaderResourceLoader {
        return this._resourceLoader;
    }

    get audioResourceLoader(): PreloaderResourceLoader {
        return this._audioResourceLoader;
    }

    public static addLoadingElementFunction(fnc: ILoadingElement): void {
        PreloaderProvider._loadingElements.push(fnc);
    }

    constructor() {
        super();
        this._resourceLoader = new PIXIResourceLoader(false);
        this._audioResourceLoader = new PIXIResourceLoader(true);
    }

    public static isGroupLoaded(group: string): boolean {
        return PreloaderProvider._loadedGroups.indexOf(group) != -1;
    }

    cleanup(): void {
        this._resourceLoader = null;
        this._audioResourceLoader = null;
        XmlResource.cleanup();
        StyleManager.cleanup();
    }

    prepareFileList(resources: Array<PreloaderResourceItem>): void {
        if (resources) {
            var gambleIsDisabled = !AppContext.gameDefinition.Gamble;

            lodash.remove(resources, (resource) => {
                var gambleResource = this.resourceHasFlag(resource, RESOURCE_FLAGS.Gamble);
                // if resource has flag 'G' (gamble) but game is disabled, remove it from resources to be loaded!
                var removeRes = gambleResource && gambleIsDisabled;

                if (DEBUG) {
                    if (removeRes) {
                        Logger.debugGroup(
                            "Preloader",
                            `Gamble disabled -> removing resource path '${resource.path}' (because has gamble flag) from file list to be loaded.`,
                        );
                    }
                }

                return removeRes;
            });
        }
    }

    loadCompleted(): Promise<any> {
        for (let i = 0; i < PreloaderProvider._loadingElements.length; i++) {
            let fnc = PreloaderProvider._loadingElements[i];
            if (fnc != null) {
                fnc.onLoadCompleted(PreloaderInfo.instance.loadSucceed);
            }
        }

        return new Promise<any>((resolve: any, reject: any) => {
            resolve();
        });
    }

    onUpdateProgress(progress: number): void {
        Platforms.Platforms.loadProgress(progress);

        for (let i = 0; i < PreloaderProvider._loadingElements.length; i++) {
            let fnc = PreloaderProvider._loadingElements[i];
            if (fnc != null) {
                fnc.onProgressUpdate(progress);
            }
        }
    }

    loadGroupCompleted(group: string): void {
        Logger.debugGroup("loadGroupCompleted", `Loaded group: ${group}`);

        if (PreloaderProvider._loadedGroups.indexOf(group) == -1) {
            PreloaderProvider._loadedGroups.push(group);
        }
    }

    handleGroupError(error: any, group: string): void {
        //todo
    }

    parseCurrentElements(): void {
        const xmlItems = XmlResource.Items;

        if (xmlItems != null && xmlItems.length > 0) {
            const tempElements = lodash.map(xmlItems, (item) => {
                const el = item.documentElement;
                const prio = el.hasAttribute("Priority") ? parseInt(el.getAttribute("Priority")) : -1;
                if (prio === -1) {
                    if (DEBUG) {
                        Logger.errorGroup("Preloader", `Element does not have "Priority" attr!`);
                    }
                }
                return { element: el, priority: prio };
            });

            const elements = lodash.map(
                lodash.orderBy(tempElements, (item) => item.priority, "desc"),
                (x) => x.element,
            );

            // 1st) parse: config/sound/style elements
            if (DEBUG) {
                Logger.debugGroup("Preloader", `Parse Elements started: ${new Date().toString()}`);
            }

            for (var i = 0; i < elements.length; i++) {
                var element = elements[i];
                ConfigurationManager.parseElements(element);
                SoundManager.parseElements(element);
            }

            for (var i = 0; i < elements.length; i++) {
                var element = elements[i];
                StyleManager.parseElements(element);
            }

            for (var i = 0; i < elements.length; i++) {
                var element = elements[i];
                ActionManager.parseActionSets(element);
            }

            // 2nd) set correct language for body, as GameApp.handleStringsLoadCompleted may be skipped because of non-existing AppContext
            document.body.setAttribute("Language", GameApp.language);

            // 3rd) parse: canvas/html elements
            for (var i = 0; i < elements.length; i++) {
                var element = elements[i];
                CanvasManager.parseElements(element);
                HtmlManager.parseElements(element);
            }
            if (DEBUG) {
                Logger.debugGroup("Preloader", `Parse Elements ended: ${new Date().toString()}`);
            }
        }

        XmlResource.emptyItems();

        if (DEBUG) {
            Logger.debugGroup("Preloader", `Parse xml resources ended.`);
        }
    }

    parseElements(): Promise<any> {
        return new Promise<any>((resolve: any, reject: any) => {
            if (DEBUG) {
                Logger.debugGroup("Preloader", `Parse xml resources started`);
            }

            const xmlItems = XmlResource.Items;

            if (xmlItems != null && xmlItems.length > 0) {
                const tempElements = lodash.map(xmlItems, (item) => {
                    const el = item.documentElement;
                    const prio = el.hasAttribute("Priority") ? parseInt(el.getAttribute("Priority")) : -1;
                    if (prio === -1) {
                        if (DEBUG) {
                            Logger.errorGroup("Preloader", `Element does not have "Priority" attr!`);
                        }
                    }
                    return { element: el, priority: prio };
                });

                const elements = lodash.map(
                    lodash.orderBy(tempElements, (item) => item.priority, "desc"),
                    (x) => x.element,
                );

                // 1st) parse: config/sound/style elements
                if (DEBUG) {
                    Logger.debugGroup("Preloader", `Parse Elements started: ${new Date().toString()}`);
                }

                for (var i = 0; i < elements.length; i++) {
                    var element = elements[i];
                    ConfigurationManager.parseElements(element);
                    SoundManager.parseElements(element);
                }

                for (var i = 0; i < elements.length; i++) {
                    var element = elements[i];
                    StyleManager.parseElements(element);
                }

                for (var i = 0; i < elements.length; i++) {
                    var element = elements[i];
                    ActionManager.parseActionSets(element);
                }

                // 2nd) set correct language for body, as GameApp.handleStringsLoadCompleted may be skipped because of non-existing AppContext
                document.body.setAttribute("Language", GameApp.language);

                // 3rd) parse: canvas/html elements
                for (var i = 0; i < elements.length; i++) {
                    var element = elements[i];
                    CanvasManager.parseElements(element);
                    HtmlManager.parseElements(element);
                }
                if (DEBUG) {
                    Logger.debugGroup("Preloader", `Parse Elements ended: ${new Date().toString()}`);
                }
            }
            if (DEBUG) {
                Logger.debugGroup("Preloader", `Parse xml resources ended.`);
            }
            resolve();
        });
    }
}

export class PIXIResourceLoader extends PreloaderResourceLoader {
    private _hasPreloaderVersionId: boolean;
    private _loadSingleCompletedCallback: CommonTypes.ErrorFunction;
    private _loadCompletedCallback: CommonTypes.ErrorFunction;
    private _preloaderVersionId: string;
    private _updatedTextures: Object;
    private _loadingErrorCount: number;
    private _loadingErrorFiles: Array<string>;
    private _resourcesWithNoId: Object;

    private loader: PIXI.loaders.Loader;

    constructor(audio: boolean) {
        super(audio);

        this.loader = new PIXI.loaders.Loader();

        this._hasPreloaderVersionId = null;
        this._loadingErrorCount = 0;
        this._loadingErrorFiles = [];
        this._resourcesWithNoId = {};

        this.handleBeforeLoad = this.handleBeforeLoad.bind(this);
        this.handleLoad = this.handleLoad.bind(this);
        this.handleError = this.handleError.bind(this);
        this.handleComplete = this.handleComplete.bind(this);
        this.handleGroupComplete = this.handleGroupComplete.bind(this);
        this.updateTextureAfterLoad = this.updateTextureAfterLoad.bind(this);
    }

    cleanup(): void {
        this._loadCompletedCallback = null;
        this._updatedTextures = null;
        this._preloaderVersionId = null;
        this._loadingErrorCount = 0;
        this._loadingErrorFiles = [];
        this._resourcesWithNoId = null;

        this.loader.reset();
    }

    xmlsResourceLoaded(document: XMLDocument, resource: PreloaderResourceItem): void {
        const acceptedAsXmlResource = document && document.documentElement.nodeName === XmlResource.Root;
        if (DEBUG) {
            const extra = acceptedAsXmlResource ? " and is accepted as xml resource for app" : "";

            Logger.debugGroup("Preloader", `Resource "${resource.path}" is loaded as xml document` + extra);
        }

        if (acceptedAsXmlResource) {
            XmlResource.add(document);
        }
    }

    beforeResourcesLoad(): void {
        this.loader.reset();
        this._updatedTextures = {};
        this.loader.use(this.updateTextureAfterLoad);
    }

    updateTextureAfterLoad(resource: PIXI.loaders.Resource, next: Function): void {
        if (resource.data && (resource as any).isImage) {
            if (resource.texture && resource.texture.baseTexture && resource.texture.baseTexture.hasLoaded) {
                const imageUrl = resource.texture.baseTexture.imageUrl;
                const noId = this._resourcesWithNoId.hasOwnProperty(imageUrl);

                if (!this._updatedTextures.hasOwnProperty(imageUrl)) {
                    if (noId) {
                        if (DEBUG) {
                            Logger.debugGroup(
                                "Preloader",
                                `Resource '${imageUrl}' has no 'id' assigned and will not be pushed to webgl cache`,
                            );
                        }
                    } else {
                        if (resource.name.substr(0, 3) === "ANI" || resource.name.substr(0, 5) === "GROUP") {
                            StageController.updateTexture(resource.texture.baseTexture);
                            this._updatedTextures[imageUrl] = true;
                        }

                        if (DEBUG) {
                            Logger.debugGroup("Preloader", `Updated texture '${imageUrl}' to webgl cache`);
                        }
                    }
                }
            }
        }

        try {
            next();
        } catch (e) {
            this._loadCompletedCallback(e);
        }
    }

    handleBeforeLoad(resource: PIXI.loaders.Resource, next: Function): void {
        if (this._hasPreloaderVersionId) {
            const oldurl = resource.url;
            resource.url = UriHelper.setTimeStamp(resource.url, this._preloaderVersionId);

            if (DEBUG && resource.url !== oldurl) {
                Logger.debugGroup("Preloader", `Resource url "${oldurl}" changed to non-cached url "${resource.url}"`);
            }
        }

        try {
            next();
        } catch (e) {
            this._loadCompletedCallback(e);
        }
    }

    handleLoad(loader: any, resource: PIXI.loaders.Resource): void {
        if (DEBUG) {
            Logger.debugGroup(
                "Preloader",
                `Resource "${resource.url}" [${resource.xhrType}] [${this.preloader.progressPercent} %]`,
            );
        }

        if (resource.xhrType === DOCUMENT_TYPE) {
            const xmlRoot: string = resource.data.documentElement.nodeName;
            if (xmlRoot === XmlResource.Root) {
                throw new Error(`Should not be there!`);
            }
        }

        this._loadSingleCompletedCallback(false);
    }

    handleError(error: Error, loader: any, resource: PIXI.loaders.Resource): void {
        if (DEBUG) {
            Logger.errorGroup("Preloader", `Resource load error: ${resource.url}`);
        }

        this._loadingErrorCount++;
        this._loadingErrorFiles.push(resource.url);
        this._loadSingleCompletedCallback(`Failed to load ${resource.url} resource!`);
    }

    handleComplete(): void {
        if (DEBUG) {
            Logger.debugGroup("Preloader", "Completed PIXI resource load");
        }

        if (this.audio) {
            for (let key in this.loader.resources) {
                if (key.indexOf("_meta") !== -1) {
                    const sound = key.substr(0, key.length - 5);

                    if (PIXI.sound.exists(sound)) {
                        let soundSprite: PIXI.sound.Sound = PIXI.sound.find(sound);

                        if (lodash.isEmpty(soundSprite.sprites)) {
                            soundSprite.addSprites(this.loader.resources[key].data.spritemap);
                        }
                    }
                }
            }
        }
    }

    handleGroupComplete(): void {
        this.loader.removeAllListeners(PIXI_EVENTS.Load);
        this.loader.removeAllListeners(PIXI_EVENTS.Error);

        this.handleComplete();

        const loadError =
            this._loadingErrorCount === 0
                ? null
                : `Failed to load ${this._loadingErrorCount} resources!: \n${this._loadingErrorFiles.join(",\n")}`;
        this._loadingErrorCount = 0;
        this._loadingErrorFiles = []; //reset counter of error for current group
        this._loadCompletedCallback(loadError);
    }

    loadAllGroupsCompleted(): void {
        // don't need to be implemented yed
    }

    loadResources(
        resources: Array<PreloaderResourceItem>,
        resolveSingleCallback: CommonTypes.ErrorFunction,
        resolveCallback: CommonTypes.ErrorFunction,
    ): void {
        if (StageController.Renderer instanceof PIXI.CanvasRenderer) {
            if (!AppContext.gameConfiguration.CanvasFallback) {
                const loadingScreen = document.getElementById("loading");
                const element = document.getElementById("progressNumber");

                if (loadingScreen != null) {
                    loadingScreen.classList.add("_error");
                }
                if (element != null) {
                    element.innerHTML = "WebGL error. Please contact support.";
                }

                return;
            }
        }

        this._loadSingleCompletedCallback = resolveSingleCallback;
        this._loadCompletedCallback = resolveCallback;

        this._preloaderVersionId = this.preloader.versionId;
        this._hasPreloaderVersionId = !StringUtils.isNullOrEmpty(this._preloaderVersionId);

        for (let i = 0; i < resources.length; i++) {
            const resource = resources[i];
            let path = resource.path;

            if (path.indexOf("{LANG}") != -1) {
                let language = "EN";
                //TODO add proper 4 letter language support to narrator
                if (GameApp.language.indexOf("-") == -1 || GameApp.language.indexOf("BR") != -1) {
                    language = GameApp.language.toUpperCase();
                } else {
                    language = GameApp.language.substring(0, GameApp.language.indexOf("-")).toUpperCase();
                }
                CustomUtils.soundsPath = path;

                if (AppContext.gameConfiguration.NarratorLanguages.indexOf(language) == -1) {
                    language = "EN";
                }

                path = path.replace(new RegExp("{LANG}", "g"), language);
            }

            if (StringUtils.isNullOrEmpty(resource.id)) {
                this.loader.add(path);
                this._resourcesWithNoId[path] = true;
            } else {
                this.loader.add(resource.id, path);
            }
        }

        this.loader.pre(this.handleBeforeLoad);
        this.loader.on("load" /*PIXI_EVENTS.Load*/, this.handleLoad);
        this.loader.on("error" /*PIXI_EVENTS.Error*/, this.handleError);
        this.loader.once("complete" /*PIXI_EVENTS.Complete*/, this.handleGroupComplete);
        this.loader.load();
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Managers/PreloaderProvider.ts
 **/